"use client"

import { HttpTypes } from "@medusajs/types"
import { clx } from "@modules/common/components/ui"
import Image from "next/image"
import { useState } from "react"

import Lightbox from "./lightbox"

type ImageGalleryProps = {
  images: HttpTypes.StoreProductImage[]
}

const ImageGallery = ({ images }: ImageGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  const validImages = images.filter((image) => !!image.url)

  if (!validImages.length) {
    return null
  }

  const currentIndex = Math.min(selectedIndex, validImages.length - 1)
  const currentImage = validImages[currentIndex]

  return (
    <div className="flex flex-col gap-y-4 small:mx-16" data-testid="image-gallery">
      <button
        type="button"
        onClick={() => setLightboxOpen(true)}
        aria-label="View image fullscreen"
        className="group relative aspect-square w-full overflow-hidden rounded-rounded border border-ui-border-base bg-white cursor-zoom-in"
        data-testid="image-gallery-main"
      >
        <Image
          src={currentImage.url!}
          priority
          className="absolute inset-0 object-contain object-center p-6 transition-transform duration-300 ease-out motion-safe:group-hover:scale-[1.02]"
          alt={`Product image ${currentIndex + 1}`}
          fill
          sizes="(max-width: 576px) 280px, (max-width: 768px) 360px, (max-width: 992px) 480px, 800px"
        />
      </button>
      {validImages.length > 1 && (
        <ul
          className="flex gap-2 overflow-x-auto pb-1"
          data-testid="image-gallery-thumbnails"
        >
          {validImages.map((image, index) => (
            <li key={image.id} className="shrink-0">
              <button
                type="button"
                onClick={() => setSelectedIndex(index)}
                aria-label={`Show image ${index + 1}`}
                aria-current={index === currentIndex}
                className={clx(
                  "relative h-20 w-20 overflow-hidden rounded-base border bg-white transition-colors",
                  index === currentIndex
                    ? "border-amber-500 ring-1 ring-amber-500"
                    : "border-ui-border-base hover:border-grey-40"
                )}
              >
                <Image
                  src={image.url!}
                  className="absolute inset-0 object-contain object-center p-1"
                  alt={`Product thumbnail ${index + 1}`}
                  fill
                  sizes="80px"
                />
              </button>
            </li>
          ))}
        </ul>
      )}
      {lightboxOpen && (
        <Lightbox
          images={validImages}
          initialIndex={currentIndex}
          onClose={(index) => {
            setSelectedIndex(index)
            setLightboxOpen(false)
          }}
        />
      )}
    </div>
  )
}

export default ImageGallery
